/* eslint-disable react/no-danger */

import React, { Component } from 'react';
import PropTypes from 'prop-types';

import PlayButton from '../../../../../components/chat-tools-fool-componets/PlayButton.jsx';
import PauseButton from '../../../../../components/chat-tools-fool-componets/PauseButton.jsx';
import FullScreenButton from '../../../../../components/chat-tools-fool-componets/FullScreenButton.jsx';
import SoundButton from '../../../../../components/chat-tools-fool-componets/SoundButton.jsx';

import {
  convertSecondsToTime, fullScreen, defineClientX, defineClientY,
} from './utils.js';
import getActualEventName from '../../../../../libs/utils/getActualEventName'

import './MediaPlayer.scss';

export default class MediaPlayer extends Component {
  constructor() {
    super();
    this.state = {
      currentTime: '0:00',
      totalTime: '',
      playingProgress: 0,
      heightToWidthRatioOfVideo: '56.25%',
      isPlaying: false,
      volume: {
        isShowControl: false,
        level: 100,
        isChanging: false,
      },
    };

    this.progressSlider = React.createRef();
    this.volumeControls = React.createRef();
    this.volumeSlider = React.createRef();
    this.player = React.createRef();
    this.progress = React.createRef();
  }

  componentDidMount() {
    this.progressSlider.current.addEventListener(getActualEventName('mousedown'), this.handleStartChangeProgress);
    this.volumeSlider.current.addEventListener(getActualEventName('mousedown'), this.handleStartChangeVolume);
    this.player.current.addEventListener('timeupdate', this.onTimeUpdate);
    this.player.current.addEventListener('loadedmetadata', () => {
      if (!this.player.current.duration) return;

      if (this.player.current.duration === Infinity) {
        this.progressSlider.current.removeEventListener(
          getActualEventName('mousedown'), this.handleStartChangeProgress,
        );
      } else {
        this.setState({ totalTime: convertSecondsToTime(this.player.current.duration) });
      }

      if (this.player.current.videoWidth) {
        const newHeightToWidthRatioOfVideo = `${
          this.player.current.videoHeight / this.player.current.videoWidth * 100}%`;
        this.setState({ heightToWidthRatioOfVideo: newHeightToWidthRatioOfVideo });
      }
    });
    this.player.current.addEventListener('ended', () => {
      this.setState({ currentTime: '0:00', isPlaying: false, playingProgress: 0 });
    });
  }

  onTimeUpdate = () => {
    if (!this.player.current) return;

    let current = this.player.current.currentTime;
    let percent = current / this.player.current.duration * 100;

    this.setState({
      currentTime: convertSecondsToTime(current),
      playingProgress: percent.toFixed(2),
    });
  }

  onTogglePlay = () => {
    const { isPlaying } = this.state;

    if (isPlaying) {
      this.player.current.pause();
    } else {
      this.player.current.play();
    }

    this.setState({ isPlaying: !isPlaying });
  }

  /* progress */
  handleStartChangeProgress = event => {
    this.handleUpdateProgress(event);
    window.addEventListener(getActualEventName('mousemove'), this.handleUpdateProgress);
    window.addEventListener(getActualEventName('mouseup'), this.handleFinishChangeProgress);
  };

  handleFinishChangeProgress = (/* event */) => {
    window.removeEventListener(getActualEventName('mousemove'), this.handleUpdateProgress);
    window.removeEventListener(getActualEventName('mouseup'), this.handleFinishChangeProgress);
  };

  handleUpdateProgress = event => {
    if (!this.player.current.duration) return;

    const progress = defineClientX(event, this.progressSlider.current);

    this.setState({
      playingProgress: progress * 100,
      currentTime: convertSecondsToTime(this.player.current.duration * progress),
    });
    this.player.current.currentTime = (this.player.current.duration * progress).toFixed(2);
  }

  /* volume */
  onToggleVolumeControl = () => {
    const { volume } = this.state;
    if (volume.isShowControl) {
      window.removeEventListener(getActualEventName('mouseup'), this.handleFinishChangeVolume);
    } else {
      window.addEventListener(getActualEventName('mouseup'), this.handleFinishChangeVolume);
    }
    this.setState({ volume: { ...volume, isShowControl: !volume.isShowControl } });
  };

  handleStartChangeVolume = event => {
    this.handleUpdateVolume(event);
    window.addEventListener(getActualEventName('mousemove'), this.handleUpdateVolume);
  };

  handleFinishChangeVolume = event => {
    const { volume } = this.state;
    if (volume.isChanging) {
      window.removeEventListener(getActualEventName('mousemove'), this.handleUpdateVolume);
      this.setState({ volume: { ...volume, isChanging: false } });
    } else {
      const isOnVolumeControls = this.volumeControls.current.contains(event.target);
      if (!isOnVolumeControls) {
        this.onToggleVolumeControl();
      }
    }
  };

  handleUpdateVolume = event => {
    const { volume } = this.state;
    let progress = defineClientY(event, this.volumeSlider.current);
    this.setState({ volume: { ...volume, level: progress * 100, isChanging: true } });
    this.player.current.volume = progress;
  };

  render() {
    const {
      currentTime, totalTime, playingProgress, isPlaying, volume, heightToWidthRatioOfVideo,
    } = this.state;
    const {
      isAudio, isVideo, src,
    } = this.props;
    const isMediaPlayerSupported = this.player.current && this.player.current.duration
      && this.player.current.duration !== Infinity && (isAudio || this.player.current.videoHeight > 0);

    let player = null;
    if (isVideo) {
      player = (
        <div className="video-wrapper">
          <div className="before-video" style={{ paddingTop: heightToWidthRatioOfVideo }} />
          <video ref={this.player} onClick={this.onTogglePlay}>
            <source src={src} />
            <track kind="captions" />
          </video>
        </div>
      );
    } else if (isAudio) {
      player = (
        <audio ref={this.player}>
          <source src={src} />
          <track kind="captions" />
        </audio>
      );
    }

    return (
      <div
        className={`media-player ${isAudio ? 'audio' : ''}`}
        dir="ltr"
        style={{ display: isMediaPlayerSupported ? '' : 'none' }}
      >
        <div className={isVideo ? 'control-wrapper-video' : 'control-wrapper'}>
          <div className="play-pause-btn" onClick={this.onTogglePlay}>
            {isPlaying ? <PauseButton /> : <PlayButton /> }
          </div>

          <div className="controls">
            <span className="current-time">
              {currentTime}
            </span>
            <div
              className="slider"
              data-direction="horizontal"
              ref={this.progressSlider}
            >
              <div className="progress" style={{ width: `${playingProgress}%` }} ref={this.progress}>
                <div className="pin" id="progress-pin" data-method="rewind" />
              </div>
            </div>
            <span className="total-time">
              {totalTime}
            </span>
            <span className="time-for-small-screens">
              {isPlaying || currentTime !== '0:00' ? currentTime : totalTime}
            </span>
          </div>

          {isVideo && <FullScreenButton onFullScreen={() => fullScreen(this.player.current)} />}

          <div className="volume" ref={this.volumeControls}>
            <div
              className={`volume-btn ${volume.isShowControl ? 'open' : ''}`}
              onClick={this.onToggleVolumeControl}
            >
              <li className="sound-btn sound-on">
                <SoundButton volumeLevel={volume.level} />
              </li>
            </div>
            <div className={`volume-controls ${volume.isShowControl ? '' : 'hidden'}`}>
              <div
                className="slider"
                data-direction="vertical"
                ref={this.volumeSlider}
              >
                <div className="progress" style={{ height: `${volume.level}%` }}>
                  <div className="pin" id="volume-pin" data-method="changeVolume" />
                </div>
              </div>
            </div>
          </div>
        </div>
        {player}
      </div>
    );
  }
}

if (process.env.NODE_ENV !== 'production') {
  MediaPlayer.propTypes = {
    src: PropTypes.string.isRequired,
    isAudio: PropTypes.bool,
    isVideo: PropTypes.bool,
  };
}
